import { useState } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6i6n7l4ieqv1pmm5gmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Info */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#4A1C40] mb-6" style={{ fontFamily: 'Crimson Pro, serif' }}>
              Vamos Conversar Sobre Seu Caso
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Se você teve seus direitos trabalhistas violados, não espere mais. Entre em contato para uma análise inicial 
              da sua situação. O primeiro passo para a justiça é buscar orientação adequada.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961] rounded-lg flex-shrink-0">
                  <i className="ri-whatsapp-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#4A1C40] mb-1">WhatsApp</h3>
                  <a 
                    href="https://wa.me/5532998566774" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-[#C9A961] transition-colors"
                  >
                    (32) 99856-6774
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961] rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#4A1C40] mb-1">E-mail</h3>
                  <a 
                    href="mailto:Barbaramarcelinoadv@hotmail.com"
                    className="text-gray-600 hover:text-[#C9A961] transition-colors"
                  >
                    Barbaramarcelinoadv@hotmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961] rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#4A1C40] mb-1">Localização</h3>
                  <p className="text-gray-600">Juiz de Fora - MG</p>
                  <p className="text-gray-500 text-sm mt-1">Atendimento online em todo Brasil</p>
                </div>
              </div>
            </div>

            <div className="bg-[#4A1C40] rounded-xl p-6">
              <h3 className="text-lg font-bold text-[#C9A961] mb-3" style={{ fontFamily: 'Crimson Pro, serif' }}>
                Horário de Atendimento
              </h3>
              <div className="space-y-2 text-white text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <p className="text-white font-bold text-sm">Segunda a Sexta: 8h às 18h</p>
                <p className="text-gray-300 text-xs">Atendimentos mediante agendamento prévio</p>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            <h3 className="text-2xl font-bold text-[#4A1C40] mb-6" style={{ fontFamily: 'Crimson Pro, serif' }}>
              Solicite uma Consulta
            </h3>

            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Seu nome completo"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefone/WhatsApp *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                  Assunto *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Selecione o assunto</option>
                  <option value="Demissão sem justa causa">Demissão sem justa causa</option>
                  <option value="Horas extras não pagas">Horas extras não pagas</option>
                  <option value="Verbas rescisórias">Verbas rescisórias</option>
                  <option value="Acidente de trabalho">Acidente de trabalho</option>
                  <option value="Desvio/Acúmulo de função">Desvio/Acúmulo de função</option>
                  <option value="Insalubridade/Periculosidade">Insalubridade/Periculosidade</option>
                  <option value="Assédio moral">Assédio moral</option>
                  <option value="Outro assunto">Outro assunto</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Descreva sua situação *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Conte brevemente sobre sua situação (máximo 500 caracteres)"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caracteres</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#4A1C40] to-[#5A2C50] text-white py-4 rounded-lg font-semibold hover:from-[#5A2C50] hover:to-[#6A3C60] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  Mensagem enviada com sucesso! Entrarei em contato em breve.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Erro ao enviar mensagem. Tente novamente ou entre em contato via WhatsApp.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Ao enviar este formulário, você concorda com o tratamento dos seus dados pessoais 
                conforme a LGPD, exclusivamente para fins de contato profissional.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}